import { FunctionComponent, ReactNode } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import ModalHeader from "../../../../shared/modalHeader"
import { TrexNavigator } from "../../../../../classes/navigator/navigator"
import { useQuery } from "@apollo/client"
import {
    ErrorMessage,
    SyncLoaderComponent,
} from "../../../../shared/graphQlResponse"
import {
    SUBSCRIPTIONS_ADD_QUERY,
    SUBSCRIPTIONS_ADMIN_ADD_QRY,
} from "../../../../../graphql/queries/subscriptionsAddQuery"
import styles from "./addSubscriptions.module.scss"
import AddSubscriptionForm from "./formContainer"
import { useNotificationsContext } from "../../../context/notificationsContext"
import {
    ManageSubscriptionSubcriberOptionsT,
    ManageSubscriptionViewerActionT,
} from "../manageSubscriptionFormUtil/types"
import { useViewerContext } from "../../../../../context/ViewerContext"
import { canRoleAdminSubscriptions } from "../../../../../util/rolePermissions/canRole"
import { UnauthorizedModalBody } from "../../../../UnauthorizedModal"
import { SubscriptionAdminAddQryQuery } from "../../../../../generated/graphql"
import { LabelValueOptionType } from "../../../../../types/commonTypes"

interface AddSubscriptionI {
    viewerAction: ManageSubscriptionViewerActionT
}

const AddSubscription: FunctionComponent<AddSubscriptionI> = ({
    viewerAction,
}) => {
    const notificationsContext = useNotificationsContext()
    const organizationId = notificationsContext.selectedOrganization.value

    return (
        <div className={styles.container}>
            <ModalHeader
                trexNavigator={
                    new TrexNavigator(
                        {
                            navigateTo: -1,
                            locationKey: useLocation().key,
                            pathName: useLocation().pathname,
                        },
                        useNavigate()
                    )
                }
                title="Add Subscription"
            />
            <div className={styles.container}>
                {viewerAction === "admin" ? (
                    <AddSubscriptionAdmin
                        viewerAction={viewerAction}
                        contextOrgId={organizationId}
                    />
                ) : (
                    <AddSubscriptionSelf
                        viewerAction={viewerAction}
                        contextOrgId={organizationId}
                    />
                )}
            </div>
        </div>
    )
}

export default AddSubscription

interface AddSubscriptionAdminI {
    viewerAction: ManageSubscriptionViewerActionT
    // contextOrgId is the organization id that the user is adding a subscription for.
    contextOrgId: string
}

const AddSubscriptionAdmin: FunctionComponent<AddSubscriptionAdminI> = ({
    viewerAction,
    contextOrgId,
}) => {
    const { role: viewerRole } = useViewerContext().getViewer()

    const { error, loading, data } = useQuery(SUBSCRIPTIONS_ADMIN_ADD_QRY, {
        variables: {
            orgId: contextOrgId,
            usersFirst: 500,
        },
        fetchPolicy: "network-only",
    })

    // check to see if the viewer has the permissions to admin subscriptions
    if (!canRoleAdminSubscriptions(viewerRole)) {
        return <UnauthorizedModalBody />
    }

    let addSubscriptionFormComponent: ReactNode = <></>
    if (loading) {
        addSubscriptionFormComponent = (
            <SyncLoaderComponent outSideDivStyle={styles.queryLoading} />
        )
    } else if (error) {
        addSubscriptionFormComponent = (
            <ErrorMessage
                outSideDivStyle={styles.queryError}
                message="We had a problem while fetching your subscription information."
            />
        )
    } else if (data) {
        // build the rule options
        const ruleOptions =
            data.rules?.rules?.map((r) => {
                return { label: r.name, value: r.id }
            }) ?? []

        // build the default subscriber well options ( which will be used if the user is not managed). Filtering of unlinked wells is done in the query.
        let subscriberWellOptions: LabelValueOptionType[] =
            data.wells?.nodes?.map((w) => {
                return {
                    label: w.name,
                    value: w.id,
                }
            }) ?? []

        // get the users that are available to add a subscription for
        const users = (data as SubscriptionAdminAddQryQuery).users?.nodes

        // build the subscriber options for the modal.
        const subscriberOptions: ManageSubscriptionSubcriberOptionsT[] = users
            .filter((item) => item.identity) // filter users without identity
            .map((user) => {
                // Under single org model users only have one organization
                const subscriberMembership = user.organizationMemberships[0]
                // if the user is managed, we need to get their well permissions
                // filter out unlinked wells
                if (subscriberMembership.managedAccess) {
                    subscriberWellOptions = subscriberMembership.wellPermissions
                        .filter((w) => w.well.linked)
                        .map((w) => {
                            const option: LabelValueOptionType = {
                                label: w.well.name,
                                value: w.well.id,
                            }

                            return option
                        })
                }

                const subscriber: ManageSubscriptionSubcriberOptionsT = {
                    name: user.identity?.fullName as string,
                    subscriberId: user.id,
                    email: user.identity?.email,
                    emailVerified: user.identity?.emailVerified || false,
                    emailConsent: user.identity?.emailConsent || false,
                    phone: user.identity?.telephoneNumber?.formatted,
                    phoneVerified:
                        user.identity?.telephoneNumberVerified || false,
                    phoneConsent: user.identity?.smsConsent || false,
                    subscriberWellPermissions: subscriberWellOptions,
                    isManagedMember: subscriberMembership.managedAccess,
                }

                return subscriber
            })

        if (!ruleOptions || ruleOptions.length === 0) {
            addSubscriptionFormComponent = (
                <div className={styles.empty}>
                    You do not have any rules to create subscriptions for.
                </div>
            )
        } else if (subscriberOptions.length === 0) {
            addSubscriptionFormComponent = (
                <div className={styles.empty}>
                    You do not have any users to create subscriptions for.
                </div>
            )
        } else {
            addSubscriptionFormComponent = (
                <AddSubscriptionForm
                    ruleOptions={ruleOptions}
                    viewerAction={viewerAction}
                    subscriberOptions={subscriberOptions}
                />
            )
        }
    }

    return <>{addSubscriptionFormComponent}</>
}

interface AddSubscriptionSelfI {
    viewerAction: ManageSubscriptionViewerActionT
    contextOrgId: string
}

const AddSubscriptionSelf: FunctionComponent<AddSubscriptionSelfI> = ({
    viewerAction,
    contextOrgId,
}) => {
    const {
        identity: viewerIdentity,
        userId: viewerUserId,
        organizationMembership: viewerOrganizationMembership,
        getPhoneNumber,
    } = useViewerContext().getViewer()

    const { error, loading, data } = useQuery(SUBSCRIPTIONS_ADD_QUERY, {
        variables: {
            orgId: contextOrgId,
        },
        fetchPolicy: "network-only",
    })

    let addSubscriptionFormComponent: ReactNode = <></>
    if (loading) {
        addSubscriptionFormComponent = (
            <SyncLoaderComponent outSideDivStyle={styles.queryLoading} />
        )
    } else if (error) {
        addSubscriptionFormComponent = (
            <ErrorMessage
                outSideDivStyle={styles.queryError}
                message="We had a problem while fetching your subscription information."
            />
        )
    } else if (data) {
        // build the rule options
        const ruleOptions = data.rules?.rules?.map((r) => {
            return { label: r.name, value: r.id }
        })

        // well permissions will respect managed memberships b/c wells query scopes by well permissions for the viewer.
        // filtering of unlinked wells is done in the query.
        const wellPermissions =
            data.wells?.nodes?.map((w) => {
                return {
                    label: w.name,
                    value: w.id,
                }
            }) ?? []

        const subscriberOptions: ManageSubscriptionSubcriberOptionsT[] = [
            {
                name: viewerIdentity.fullName,
                subscriberId: viewerUserId,
                email: viewerIdentity.email,
                emailVerified: viewerIdentity.emailVerified,
                emailConsent: viewerIdentity.emailConsent,
                phone: getPhoneNumber(true),
                phoneVerified: viewerIdentity.telephoneNumberVerified,
                phoneConsent: viewerIdentity.smsConsent,
                subscriberWellPermissions: wellPermissions,
                isManagedMember: viewerOrganizationMembership.managedAccess,
            },
        ]

        if (!ruleOptions || ruleOptions.length === 0) {
            addSubscriptionFormComponent = (
                <div className={styles.empty}>
                    You do not have any rules to create subscriptions for.
                </div>
            )
        } else if (
            subscriberOptions[0].subscriberWellPermissions.length === 0
        ) {
            addSubscriptionFormComponent = (
                <div className={styles.empty}>
                    You do not have any wells to create subscriptions for.
                </div>
            )
        } else {
            addSubscriptionFormComponent = (
                <AddSubscriptionForm
                    ruleOptions={ruleOptions}
                    viewerAction={viewerAction}
                    subscriberOptions={subscriberOptions}
                />
            )
        }
    }

    return <>{addSubscriptionFormComponent}</>
}
